import React, { useEffect, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Dialog from "common/basicComponents/Dialog";
import Typography, {
  TypographyVariant,
  TypographyWeight,
} from "common/basicComponents/Typography";
import Button, {
  ButtonVariant,
  ButtonType,
} from "common/basicComponents/Button";
import Box from "common/basicComponents/Box";
import LabelledCheckbox, {
  CheckboxType,
} from "common/basicComponents/LabelledCheckbox";
import { useViewport, ViewportType } from "utils/ViewportContext";

const Buttons = styled.div`
  display: flex;
  flex-direction: ${({ viewport }) =>
    viewport > ViewportType.MOBILE ? "row" : "column-reverse"};
  margin-top: 2.5rem;
  width: 100%;
`;

// Resumes a paused subscription. The paused months are NEVER charged
// automatically — they're either kept as drafts (collect manually later) or
// voided (forgiven) per the toggle.
const UnpauseSubscriptionDialog = ({ open, onClose, onConfirm }) => {
  const { t } = useTranslation("billing");
  const { type: viewportType } = useViewport();
  const [voidDrafts, setVoidDrafts] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setVoidDrafts(false);
  }, [open]);

  const confirm = async () => {
    setSubmitting(true);
    await onConfirm(voidDrafts);
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Box padding="0 0 1.5rem">
        <Typography
          variant={TypographyVariant.H4}
          weight={TypographyWeight.BOLD}
          text={t("resumeSubscriptionTitle")}
        />
      </Box>
      <Box padding="0 0 1rem">
        <Typography
          variant={TypographyVariant.BODY1}
          text={
            voidDrafts
              ? t("unpauseVoidDescription")
              : t("unpauseKeepDescription")
          }
        />
      </Box>
      <LabelledCheckbox
        id="voidDrafts"
        type={CheckboxType.CONTAINED}
        label={t("unpauseVoidLabel")}
        value={voidDrafts}
        onChange={setVoidDrafts}
      />
      <Buttons viewport={viewportType}>
        <Box
          width={viewportType < ViewportType.TABLET ? "100%" : "50%"}
          padding={
            viewportType < ViewportType.TABLET ? "0.625rem 0" : "0 1rem 0 0"
          }
        >
          <Button
            variant={ButtonVariant.OUTLINED}
            text={t("common:button.cancel")}
            minWidth="13rem"
            width="100%"
            onClick={onClose}
            disabled={submitting}
          />
        </Box>
        <Box
          width={viewportType < ViewportType.TABLET ? "100%" : "50%"}
          padding={
            viewportType < ViewportType.TABLET ? "0.625rem 0" : "0 0 0 1rem"
          }
        >
          <Button
            variant={ButtonVariant.GREEN}
            text={t("resumeSubscription")}
            minWidth="13rem"
            width="100%"
            type={ButtonType.BUTTON}
            onClick={confirm}
            isLoading={submitting}
          />
        </Box>
      </Buttons>
    </Dialog>
  );
};

UnpauseSubscriptionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default UnpauseSubscriptionDialog;
